import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronDown, LogOut } from "lucide-react";
import { useTicketContext } from "../context/TicketContext.js";
import { UserAuth } from "../context/AuthContext.js";
import "./navbar.css";

/**
 * UserMenu — operator-hub style avatar/email/chevron trigger that opens a
 * dropdown showing the email, admin chip, and a Sign out action.
 * Defined alongside Navbar (not exported) since it's only used here.
 */
function UserMenu() {
  const { user, handlerLogout } = UserAuth();
  const [open, setOpen] = useState(false);
  const triggerRef = useRef(null);
  const dropdownRef = useRef(null);

  // close on outside click
  useEffect(() => {
    const onClick = (e) => {
      if (!open) return;
      if (
        dropdownRef.current?.contains(e.target) ||
        triggerRef.current?.contains(e.target)
      ) {
        return;
      }
      setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  // close on Escape
  useEffect(() => {
    const onKey = (e) => {
      if (open && e.key === "Escape") setOpen(false);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);

  const email = user?.email || "";
  const initial = email ? email.charAt(0).toUpperCase() : "U";

  return (
    <div className="ophub-usermenu">
      <button
        ref={triggerRef}
        type="button"
        className="ophub-usermenu__trigger"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <div className="ophub-usermenu__avatar">{initial}</div>
        <div className="ophub-usermenu__meta">
          <span className="ophub-usermenu__email">{email || "User"}</span>
          {user?.isAdmin && <span className="ophub-usermenu__role">Admin</span>}
        </div>
        <ChevronDown size={16} className="ophub-usermenu__caret" />
      </button>

      {open && (
        <div ref={dropdownRef} className="ophub-usermenu__dropdown" role="menu">
          <div className="ophub-usermenu__dropdown-header">
            <span className="ophub-usermenu__dropdown-email">
              {email || "User"}
            </span>
            {user?.isAdmin && (
              <span className="ophub-usermenu__chip">Admin</span>
            )}
          </div>
          <div className="ophub-usermenu__dropdown-separator" />
          <button
            type="button"
            className="ophub-usermenu__dropdown-item ophub-usermenu__dropdown-item--danger"
            onClick={() => {
              setOpen(false);
              handlerLogout?.();
            }}
            role="menuitem"
          >
            <LogOut size={14} />
            Sign out
          </button>
        </div>
      )}
    </div>
  );
}

const Navbar = ({ getTask, showSearchInput }) => {
  const navigate = useNavigate();
  const { getTickets } = useTicketContext();
  const [textBoxValue, setTextBoxValue] = useState("");

  const handleReviewInvoice = async () => {
    try {
      if (getTask) {
        await getTask(textBoxValue);
        setTextBoxValue("");
      } else {
        await getTickets(textBoxValue);
      }
    } catch (error) {
      console.log("### something went wrong ###");
    }
  };

  return (
    <header className="ophub-topbar">
      <div className="ophub-topbar__left">
        <button
          type="button"
          className="ophub-topbar__brand"
          onClick={() => navigate("/")}
          aria-label="Go to home"
        >
          <img
            src="/sps-logo.svg"
            alt="SPS"
            className="ophub-topbar__logo-img"
          />
          <span className="ophub-topbar__app-name">HIVE</span>
        </button>
      </div>

      <div className="ophub-topbar__right">
        {showSearchInput && (
          <div className="ophub-topbar__search">
            <input
              type="text"
              className="ophub-topbar__search-input"
              placeholder="Click here to search..."
              value={textBoxValue}
              onChange={(e) => setTextBoxValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleReviewInvoice();
              }}
            />
            <button
              type="button"
              className="ophub-topbar__search-button"
              onClick={handleReviewInvoice}
            >
              Search
            </button>
          </div>
        )}
        <UserMenu />
      </div>
    </header>
  );
};

export default Navbar;
